import { FavoriteFontModel } from "../types/customFontTypes";

const CACHE_KEY = "ezy_favorites_cache";

let writePromise: Promise<void> = Promise.resolve();
let latestFavoritesPending: { uid: string; favorites: FavoriteFontModel[] } | null = null;
let isWriting = false;

export async function loadFavoritesFromCache(uid: string): Promise<FavoriteFontModel[]> {
  if (typeof caches === "undefined" || !uid) return [];
  try {
    const cache = await caches.open(CACHE_KEY);
    const res = await cache.match(`/favorites/${uid}`);
    const raw = res ? await res.json() : [];
    return Array.isArray(raw)
      ? raw.map((item: Partial<FavoriteFontModel>) => ({
          id: item.id || item.fontName || "",
          fontName: item.fontName || "",
          displayName: item.displayName || item.fontName || "",
          isCustom: Boolean(item.isCustom),
          fileName: item.fileName,
          dateFavorited: item.dateFavorited || Math.floor(Date.now() / 1000),
        }))
      : [];
  } catch (err) {
    console.warn("Failed to load favorites from Cache Storage:", err);
    return [];
  }
}

export function saveFavoritesToCache(
  uid: string,
  favorites: FavoriteFontModel[]
): Promise<void> {
  if (typeof caches === "undefined" || !uid) return Promise.resolve();

  // 1. Always store the freshest snapshot of data to write
  latestFavoritesPending = { uid, favorites };

  // 2. If a write is already in progress, the active loop will pick up this latest snapshot
  if (isWriting) {
    return writePromise;
  }

  // 3. Process write queue sequentially (eliminates race conditions)
  isWriting = true;
  writePromise = (async () => {
    try {
      while (latestFavoritesPending) {
        const current = latestFavoritesPending;
        latestFavoritesPending = null;

        const cache = await caches.open(CACHE_KEY);
        const minimal = current.favorites.map((f) => ({
          fontName: f.fontName,
          displayName: f.displayName || f.fontName,
          isCustom: f.isCustom,
          fileName: f.fileName,
        }));

        await cache.put(
          `/favorites/${current.uid}`,
          new Response(JSON.stringify(minimal), {
            headers: { "Content-Type": "application/json" },
          })
        );
      }
    } catch (err) {
      console.warn("Failed to save favorites to Cache Storage:", err);
    } finally {
      isWriting = false;
    }
  })();

  return writePromise;
}

export async function flushPendingWrites(): Promise<void> {
  if (latestFavoritesPending && !isWriting) {
    const current = latestFavoritesPending;
    await saveFavoritesToCache(current.uid, current.favorites);
  }
  return writePromise;
}

// Lifecycle listeners to flush pending writes on fast tab close or navigation
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushPendingWrites();
    }
  });
  window.addEventListener("pagehide", () => {
    void flushPendingWrites();
  });
}
